import React, { useEffect, useRef, useState } from 'react';
import { Icon, type IconName } from './ui/Icon';

export type CascadeOption = {
  id: number | string;
  name: string;
};

type CascadeComboProps<T extends CascadeOption> = {
  label: string;
  value: T | null;
  inputValue: string;
  onChange: (option: T | null) => void;
  onInputChange: (text: string, isUserInput: boolean) => void;
  options: T[];
  loading?: boolean;
  disabled?: boolean;
  disableClear?: boolean;
  required?: boolean;
  error?: string | boolean;
  helperText?: string;
  placeholder?: string;
  loadingText?: string;
  noOptionsText?: string;
  endActionIcon?: { icon: IconName; onClick: () => void; title?: string };
};

export function CascadeCombo<T extends CascadeOption>({
  label,
  value,
  inputValue,
  onChange,
  onInputChange,
  options,
  loading = false,
  disabled = false,
  disableClear = false,
  required = false,
  error,
  helperText,
  placeholder = 'Select…',
  loadingText = 'Loading…',
  noOptionsText = 'Type to search',
  endActionIcon,
}: CascadeComboProps<T>) {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [open]);

  // Focus input when opening.
  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  const displayLabel = value?.name || '';
  const errorClass = error ? { borderColor: 'var(--err)' } : {};
  const hasError = Boolean(error);
  const showHelper = (typeof error === 'string' && error) || helperText;

  return (
    <div className="field" ref={containerRef} style={{ position: 'relative' }}>
      <label className="field-label">
        {label}
        {required && <span style={{ color: 'var(--err)' }}> *</span>}
      </label>

      {!open && (
        <button
          type="button"
          disabled={disabled}
          onClick={() => setOpen(true)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            width: '100%',
            padding: '10px 12px',
            border: '1px solid var(--line-strong)',
            borderRadius: 'var(--r-md)',
            background: disabled ? 'var(--bg-tint)' : 'var(--bg-elev)',
            color: 'var(--ink)',
            fontFamily: 'inherit',
            fontSize: 14,
            cursor: disabled ? 'not-allowed' : 'pointer',
            textAlign: 'start',
            transition: 'border-color 0.15s var(--ease)',
            ...errorClass,
          }}
        >
          <span
            style={{
              flex: 1,
              minWidth: 0,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              color: displayLabel ? 'var(--ink)' : 'var(--ink-4)',
            }}
          >
            {displayLabel || placeholder}
          </span>
          {loading && (
            <Icon name="refresh" size={14} color="var(--ink-3)" />
          )}
          {!loading && value && !disableClear && !disabled && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                onChange(null);
                onInputChange('', false);
              }}
              aria-label="Clear"
              style={{
                width: 20,
                height: 20,
                border: 'none',
                background: 'transparent',
                color: 'var(--ink-4)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 4,
                padding: 0,
              }}
            >
              <Icon name="x" size={13} />
            </button>
          )}
          {endActionIcon && !disabled && (
            <button
              type="button"
              title={endActionIcon.title}
              onClick={(e) => {
                e.stopPropagation();
                endActionIcon.onClick();
              }}
              style={{
                width: 24,
                height: 24,
                border: 'none',
                background: 'transparent',
                color: 'var(--brand-blue)',
                cursor: 'pointer',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: 4,
                padding: 0,
              }}
            >
              <Icon name={endActionIcon.icon} size={14} />
            </button>
          )}
          <Icon name="chevronDown" size={14} color="var(--ink-3)" />
        </button>
      )}

      {open && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 10px',
              border: '1px solid var(--brand-blue)',
              borderRadius: 'var(--r-md)',
              background: 'var(--bg-elev)',
              boxShadow: 'var(--shd-focus)',
            }}
          >
            <Icon name="search" size={14} color="var(--ink-3)" />
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => onInputChange(e.target.value, true)}
              placeholder={placeholder}
              autoComplete="off"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontFamily: 'inherit',
                fontSize: 14,
                color: 'var(--ink)',
                padding: '4px 0',
              }}
            />
            {loading && <Icon name="refresh" size={14} color="var(--ink-3)" />}
          </div>

          <div
            style={{
              position: 'absolute',
              top: '100%',
              insetInlineStart: 0,
              insetInlineEnd: 0,
              marginTop: 6,
              maxHeight: 280,
              overflowY: 'auto',
              background: 'var(--bg-elev)',
              border: '1px solid var(--line)',
              borderRadius: 'var(--r-md)',
              boxShadow: 'var(--shd-3)',
              zIndex: 50,
              padding: 4,
            }}
            role="listbox"
          >
            {loading && options.length === 0 && (
              <div className="muted" style={{ padding: 14, textAlign: 'center', fontSize: 13 }}>
                {loadingText}
              </div>
            )}
            {!loading && options.length === 0 && (
              <div className="muted" style={{ padding: 14, textAlign: 'center', fontSize: 13 }}>
                {noOptionsText}
              </div>
            )}
            {options.map((opt) => {
              const selected = value?.id === opt.id;
              return (
                <button
                  key={String(opt.id)}
                  type="button"
                  role="option"
                  aria-selected={selected}
                  onClick={() => {
                    onChange(opt);
                    onInputChange(opt.name, false);
                    setOpen(false);
                  }}
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px 10px',
                    border: 'none',
                    background: selected ? 'var(--bg-tint)' : 'transparent',
                    color: 'var(--ink)',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: selected ? 600 : 400,
                    cursor: 'pointer',
                    textAlign: 'start',
                    borderRadius: 'var(--r-sm)',
                    transition: 'background 0.1s ease',
                  }}
                  onMouseEnter={(e) => {
                    if (!selected) (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-tint)';
                  }}
                  onMouseLeave={(e) => {
                    if (!selected) (e.currentTarget as HTMLButtonElement).style.background = 'transparent';
                  }}
                >
                  {opt.name}
                </button>
              );
            })}
          </div>
        </>
      )}

      {showHelper && (
        <span
          style={{
            fontSize: 11,
            color: hasError ? 'var(--err)' : 'var(--ink-3)',
            marginTop: 2,
          }}
        >
          {(typeof error === 'string' && error) || helperText}
        </span>
      )}
    </div>
  );
}

export default CascadeCombo;
